import { useState } from 'react';
import ImageGalleryViewer from '../../../components/feature/ImageGalleryViewer';

interface JobFullViewProps {
  job: {
    id: string;
    service_category: string;
    service_subcategory: string;
    city: string;
    postcode: string;
    address: string;
    description: string;
    is_instant: boolean;
    scheduled_date: string | null;
    created_at: string;
    images?: string[];
    customer_name?: string;
    customer_phone?: string;
  };
  onAccept: (jobId: string) => void;
  onDecline: (jobId: string) => void;
  onClose: () => void;
  accepting?: boolean;
  declining?: boolean;
}

export default function JobFullView({ 
  job, 
  onAccept, 
  onDecline, 
  onClose,
  accepting = false,
  declining = false 
}: JobFullViewProps) {
  const scheduledTime = job.scheduled_date ? new Date(job.scheduled_date) : null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 flex items-start justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-8 animate-fadeIn">
        {/* Header */}
        <div className="relative p-6 border-b border-gray-200">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>

          <div className="pr-12">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-2xl font-bold text-gray-900">{job.service_subcategory}</h2>
              {job.is_instant ? (
                <span className="px-4 py-1.5 bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-bold rounded-full flex items-center gap-2 whitespace-nowrap">
                  <i className="ri-flashlight-fill"></i>
                  INSTANT - Priority (ASAP)
                </span>
              ) : (
                <span className="px-4 py-1.5 bg-blue-100 text-blue-800 text-sm font-semibold rounded-full whitespace-nowrap">
                  SCHEDULED
                </span>
              )}
            </div>
            <p className="text-gray-600">{job.service_category}</p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 max-h-[calc(100vh-300px)] overflow-y-auto">
          {/* Timing */}
          {scheduledTime && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center">
                  <i className="ri-calendar-line text-xl"></i>
                </div>
                <div>
                  <p className="text-sm text-blue-700 font-medium">Scheduled For</p>
                  <p className="text-lg font-bold text-blue-900">
                    {scheduledTime.toLocaleDateString('en-GB', { 
                      weekday: 'long', 
                      day: 'numeric', 
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                  <p className="text-blue-700">
                    {scheduledTime.toLocaleTimeString('en-GB', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Location */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <i className="ri-map-pin-line text-[#ff751f]"></i>
              Location
            </h3>
            <div className="p-4 bg-gray-50 rounded-xl">
              <p className="font-semibold text-gray-900">{job.city}</p>
              <p className="text-gray-700">{job.postcode}</p>
              <p className="text-sm text-gray-500 mt-2">
                Full address will be revealed after accepting the job
              </p>
            </div>
          </div>

          {/* Description */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <i className="ri-file-text-line text-[#ff751f]"></i>
              Full Description
            </h3>
            <div className="p-4 bg-gray-50 rounded-xl">
              <p className="text-gray-900 whitespace-pre-wrap">{job.description || 'No description provided'}</p>
            </div>
          </div>

          {/* Images */}
          {job.images && job.images.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <i className="ri-image-line text-[#ff751f]"></i>
                Photos ({job.images.length})
              </h3>
              <ImageGalleryViewer images={job.images} />
            </div>
          )}

          {/* Posted Time */}
          <div className="text-sm text-gray-500">
            Posted on {new Date(job.created_at).toLocaleString('en-GB', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <div className="flex items-center gap-4">
            <button
              onClick={() => onDecline(job.id)}
              disabled={accepting || declining}
              className="flex-1 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {declining ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Declining...
                </span>
              ) : (
                'Decline'
              )}
            </button>
            <button
              onClick={() => onAccept(job.id)}
              disabled={accepting || declining}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-[#ff751f] to-[#ff8c3a] text-white font-bold rounded-xl hover:from-[#e56a0f] hover:to-[#ff751f] transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {accepting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Accepting...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-check-line text-xl"></i>
                  Accept Job
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
